import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaMoneyBillWave, FaCalendar, FaClipboardList, FaCheck, FaTimes, FaSpinner, FaDollarSign } from 'react-icons/fa';
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import './LoanRequests.css';

const LoanRequests = () => {
  const [loanRequests, setLoanRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const baseUrl = "https://api.burrayhr.com";

  useEffect(() => {
    fetchLoanRequests();
  }, []);

  const fetchLoanRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/loans/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLoanRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching loan requests:', error);
      showAlert('error', 'Failed to load loan requests');
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    Swal.fire({
      icon: type,
      title: type === 'success' ? 'Success!' : 'Error!',
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: type === 'success' ? '#d4edda' : '#f8d7da',
      color: type === 'success' ? '#155724' : '#721c24',
    });
  };

  const handleAction = async (loan, action) => {
    if (action === 'paid') {
      await handlePaidStatus(loan);
      return;
    }

    const { value: response } = await Swal.fire({
      title: `${action === 'approved' ? 'Approve' : 'Reject'} Loan Request`,
      html: `
        <div class="loan-details-modal">
          <div class="loan-info">
            <p><strong>Employee:</strong> ${loan.employee_name}</p>
            <p><strong>Type:</strong> ${loan.type}</p>
            <p><strong>Amount:</strong> $${loan.amount}</p>
            <p><strong>Repayment Period:</strong> ${loan.repayment_period} days</p>
            <p><strong>Reason:</strong> ${loan.reason}</p>
          </div>
          <div class="form-group">
            <label for="response-message">Response Message:</label>
            <textarea 
              id="response-message" 
              class="swal2-textarea" 
              placeholder="Enter your response message"
              required
            ></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: action === 'approved' ? 'Approve' : 'Reject',
      confirmButtonColor: action === 'approved' ? '#28a745' : '#dc3545',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const message = document.getElementById('response-message').value;
        if (!message.trim()) {
          Swal.showValidationMessage('Please enter a response message');
          return false;
        }
        return message;
      }
    });

    if (response) {
      try {
        const token = localStorage.getItem('token');
        await axios.patch(`${baseUrl}/api/loans/${loan.employee}/${loan.id}/`, {
          status: action,
          response_message: response
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        showAlert('success', `Loan request ${action} successfully`);
        fetchLoanRequests();
      } catch (error) {
        console.error('Error updating loan request:', error);
        showAlert('error', 'Failed to update loan request');
      }
    }
  };

  const handlePaidStatus = async (loan) => {
    try {
      const result = await Swal.fire({
        title: 'Mark Loan as Paid?',
        text: "Are you sure you want to mark this loan as paid?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, mark as paid'
      });

      if (result.isConfirmed) {
        const token = localStorage.getItem('token');
        await axios.put(`${baseUrl}/api/loans/${loan.employee}/${loan.id}/`, {
          is_paid: true
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        showAlert('success', 'Loan marked as paid successfully');
        fetchLoanRequests();
      }
    } catch (error) {
      console.error('Error updating paid status:', error);
      showAlert('error', 'Failed to update paid status');
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="main-content">
        <div className="loan-requests-container">
          <div className="loan-requests-header">
            <FaMoneyBillWave className="header-icon" />
            <h2>Loan Requests</h2>
          </div>

          {loading ? (
            <div className="loading-state">
              <FaSpinner className="spinner" />
              <p>Loading loan requests...</p>
            </div>
          ) : loanRequests.length === 0 ? (
            <div className="no-requests">
              <FaClipboardList className="no-requests-icon" />
              <p>No loan requests found</p>
            </div>
          ) : (
            <div className="loan-requests-grid">
              {loanRequests.map((loan) => (
                <div 
                  key={loan.id} 
                  className={`loan-request-card status-${loan.status}`}
                >
                  <div className="card-header">
                    <h3>{loan.type.charAt(0).toUpperCase() + loan.type.slice(1)}</h3>
                    <span className={`status-badge ${loan.status}`}>
                      {loan.status}
                    </span>
                  </div>
                  
                  <div className="card-body">
                    <div className="info-row">
                      <FaMoneyBillWave className="info-icon" />
                      <span className="amount">${loan.amount}</span>
                    </div>
                    
                    <div className="info-row">
                      <FaCalendar className="info-icon" />
                      <span>{loan.repayment_period} days</span>
                    </div>
                    
                    <div className="info-row reason">
                      <FaClipboardList className="info-icon" />
                      <p>{loan.reason}</p>
                    </div>

                    <div className="employee-info">
                      <span className="employee-name">{loan.employee_name}</span>
                      <span className="request-date">{new Date(loan.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="card-actions">
                    {loan.status === 'pending' && (
                      <div className="button-group">
                        <button 
                          className="action-button approve" 
                          onClick={() => handleAction(loan, 'approved')}
                        >
                          <FaCheck /> Approve
                        </button>
                        <button 
                          className="action-button reject" 
                          onClick={() => handleAction(loan, 'rejected')}
                        >
                          <FaTimes /> Reject
                        </button>
                      </div>
                    )}
                    {loan.status === 'approved' && !loan.is_paid && (
                      <button 
                        className="action-button paid" 
                        onClick={() => handleAction(loan, 'paid')}
                      >
                        <FaDollarSign /> Mark as Paid
                      </button>
                    )}
                    {loan.is_paid && (
                      <div className="paid-status">
                        <FaCheck className="paid-icon" />
                        <span>Paid</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default LoanRequests
